import { isSensitiveQueryKey, REDACTED_VALUE } from "./redaction-rules";

type UrlInputKind = "absolute" | "hash" | "protocol-relative" | "relative" | "search";

const BASE_URL = "https://xan.invalid";
const DOCUMENT_VIEW_RE = /(^|\/)document-view\/[^/?#]+/g;
const URL_PROTOCOL_RE = /^[a-z][a-z\d+\-.]*:\/\//i;

function inputKind(value: string): UrlInputKind {
  if (URL_PROTOCOL_RE.test(value)) {
    return "absolute";
  }
  // Browsers treat a backslash like a slash here, so /\host is protocol-relative.
  if (/^[/\\]{2}/.test(value)) {
    return "protocol-relative";
  }
  if (value.startsWith("?")) {
    return "search";
  }
  if (value.startsWith("#")) {
    return "hash";
  }
  return "relative";
}

function parseUrl(value: string, kind: UrlInputKind): URL {
  return new URL(kind === "protocol-relative" ? `https:${value}` : value, BASE_URL);
}

function redactSearchParams(searchParams: URLSearchParams): string {
  const next = new URLSearchParams(searchParams);
  const keysToCheck = Array.from(next.keys());
  for (const key of keysToCheck) {
    if (isSensitiveQueryKey(key)) {
      next.set(key, REDACTED_VALUE);
    }
  }
  return next.toString();
}

function sanitizeHash(hash: string): string {
  if (hash.length <= 1) {
    return hash;
  }

  const value = hash.slice(1);
  if (value.startsWith("/") || value.startsWith("?")) {
    return `#${sanitizeObservabilityUrl(value)}`;
  }
  if (!value.includes("=")) {
    return `#${value.replace(DOCUMENT_VIEW_RE, "$1document-view/[token]")}`;
  }

  return `#${redactSearchParams(new URLSearchParams(value))}`;
}

function sanitizeUrlParts(url: URL): URL {
  url.pathname = url.pathname.replace(DOCUMENT_VIEW_RE, "$1document-view/[token]");
  url.search = redactSearchParams(url.searchParams);
  url.hash = sanitizeHash(url.hash);
  return url;
}

function formatUrl(url: URL, kind: UrlInputKind): string {
  const path = `${url.pathname}${url.search}${url.hash}`;
  const formats = {
    absolute: () => url.toString(),
    hash: () => url.hash,
    "protocol-relative": () => `//${url.host}${path}`,
    relative: () => path,
    search: () => `${url.search}${url.hash}`,
  } satisfies Record<UrlInputKind, () => string>;

  return formats[kind]();
}

/** Redacts document-view tokens and sensitive query/hash params from URLs or paths. */
export function sanitizeObservabilityUrl(urlOrPath: string): string {
  if (urlOrPath.length === 0) {
    return urlOrPath;
  }

  try {
    const kind = inputKind(urlOrPath);
    return formatUrl(sanitizeUrlParts(parseUrl(urlOrPath, kind)), kind);
  } catch {
    return urlOrPath.replace(DOCUMENT_VIEW_RE, "$1document-view/[token]");
  }
}
